import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchPlacementBanners, trackBannerEvent } from '../lib/bannerApi';

/**
 * Category hero, served from Banner Management.
 *
 * Renders whatever the admin published for `placement=category_top` scoped to
 * this category. If nothing is published — or the request fails — it renders
 * nothing at all and the page continues with its text header. A banner is
 * decoration; a category page must never depend on one to be usable.
 *
 * The impression is counted once per mount and only when the banner is actually
 * on screen, so a customer who never scrolls past the fold does not inflate the
 * count for a banner they never saw.
 */
export function CategoryHeroBanner({ categoryId, placement = 'category_top', scoped = true, variant = 'full', maxHeight, onLoaded }) {
  const [banner, setBanner] = useState(null);
  // Set from the image's own dimensions once it loads. A fixed ratio was the
  // bug: the strip was 6:1 while the uploads are 2.74:1, so `object-cover`
  // silently cut away more than half the artwork — including the CTA baked
  // into the bottom of the mobile banner.
  const [ratio, setRatio] = useState(null);
  const ref = useRef(null);
  const counted = useRef(false);

  useEffect(() => {
    // A homepage slot has no category; a category slot must wait for its id
    // rather than fetch the unscoped feed and show another category's art.
    if (scoped && !categoryId) return;
    let cancelled = false;
    counted.current = false;

    fetchPlacementBanners(placement, categoryId ? { category_id: categoryId } : {})
      .then((list) => {
        if (cancelled) return;
        // The feed is already priority-ordered; take the top one.
        const top = list[0] || null;
        setBanner(top);
        onLoaded?.(!!top);
      })
      .catch(() => { if (!cancelled) onLoaded?.(false); });

    return () => { cancelled = true; };
  }, [categoryId, placement, scoped, onLoaded]);

  // Count the impression only once the banner has actually been seen.
  useEffect(() => {
    const node = ref.current;
    if (!banner || !node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !counted.current) {
        counted.current = true;
        trackBannerEvent(banner.banner_id, 'view');
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [banner]);

  if (!banner) return null;

  const desktop = banner.desktop_image_url;
  const mobile = banner.mobile_image_url || desktop;
  if (!desktop && !mobile) return null;

  const hasText = !!(banner.title || banner.subtitle || banner.cta_label);
  const onClick = () => trackBannerEvent(banner.banner_id, 'click');

  /* The browser swaps <source> at the breakpoint and fires load again, so the
     ratio follows whichever file is actually on screen — desktop 2.74:1,
     mobile 0.85:1 — without the component having to know either number. */
  const onLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    if (w && h) setRatio(w / h);
  };

  const contained = variant === 'contained';

  // Before the image loads, fall back to the desktop slot's own ratio so the
  // space is reserved and the page does not jump.
  const slotRatio = ratio || 1920 / 700;

  // Contained slots carry a default cap; full-bleed ones are uncapped unless the
  // host asks for a height.
  const capPx = maxHeight ?? (contained ? 460 : null);

  /**
   * How a height cap is honoured depends on the variant, because the art is
   * `contain`-fitted and a box shorter than the art's own ratio would otherwise
   * letterbox it behind flat background bars.
   *
   *   contained  — bound the width as well, so the whole card shrinks and
   *                nothing is cropped.
   *   full-bleed — the width *is* the viewport, so being shorter can only mean
   *                cropping. Landscape art only: the portrait mobile upload
   *                (0.85:1) carries its CTA baked into the bottom edge and
   *                loses it the instant it is cropped, which is the bug that
   *                put `object-contain` here in the first place.
   */
  const cropToCap = capPx != null && !contained && slotRatio >= 1.5;

  const art = (
    <picture>
      {/* Mobile art is portrait, so it gets its own source rather than being a
          crop of the desktop file. */}
      <source media="(max-width: 640px)" srcSet={mobile} />
      <img
        src={desktop}
        alt={banner.title || ''}
        onLoad={onLoad}
        /* Uncapped slots stay `contain`: uploads are validated to ±8% of the
           slot ratio, so a file that is a few percent off should letterbox by a
           few pixels rather than have its edges cut off. Below the cap width
           the two fits are identical — `cover` only bites once the cap does. */
        className={`h-full w-full ${cropToCap ? 'object-cover' : 'object-contain'}`}
        loading="eager"
        decoding="async"
      />
    </picture>
  );

  const body = (
    <div
      ref={ref}
      className={`relative w-full overflow-hidden ${
        contained ? 'mx-auto rounded-2xl ring-1 ring-slate-900/[0.04] shadow-[0_8px_24px_rgba(16,24,40,0.06)]' : ''}`}
      style={{
        aspectRatio: String(slotRatio),
        maxHeight: capPx ? `${capPx}px` : '70vh',
        // Width is bounded only for contained cards; a full-bleed slot must stay
        // edge to edge, which is precisely why its cap has to crop instead.
        maxWidth: capPx && contained ? `${Math.round(slotRatio * capPx)}px` : undefined,
        background: banner.background_color || '#F1F5F9',
      }}
    >
      {art}

      {Number(banner.overlay) > 0 && (
        <div className="absolute inset-0 bg-slate-900" style={{ opacity: Number(banner.overlay) / 100 }} />
      )}

      {/* Copy is overlaid only when the admin actually supplied some. Most
          category art already carries its own headline, and printing an empty
          heading over it would just be a shadow on the image. */}
      {hasText && (
        <div
          className={`absolute inset-0 flex flex-col justify-center gap-1.5 px-6 md:px-10 ${
            banner.text_align === 'center' ? 'items-center text-center'
              : banner.text_align === 'right' ? 'items-end text-right' : 'items-start'}`}
        >
          {banner.title && (
            <h2 className="font-body text-xl font-extrabold tracking-[-0.02em] text-white drop-shadow-sm md:text-3xl">
              {banner.title}
            </h2>
          )}
          {banner.subtitle && (
            <p className="max-w-xl font-body text-[13px] text-white/90 drop-shadow-sm md:text-base">
              {banner.subtitle}
            </p>
          )}
          {banner.cta_label && (
            <span className="mt-1.5 inline-flex rounded-lg bg-white px-4 py-2 font-body text-[13px] font-bold text-secondary shadow-sm">
              {banner.cta_label}
            </span>
          )}
        </div>
      )}
    </div>
  );

  // Only wrap in a link when there is somewhere to go — a cursor that promises
  // navigation and does nothing is worse than a plain image.
  const clickable = banner.cta_url
    ? <Link to={banner.cta_url} onClick={onClick} className="block">{body}</Link>
    : body;

  // Contained slots sit in the page gutter with the same rhythm as the sections
  // around them, so they read as part of the page rather than interrupting it.
  return contained
    ? <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-8">{clickable}</div>
    : clickable;
}

/**
 * Reads a placement without rendering it.
 *
 * A page that has its own fallback artwork needs to know whether a banner
 * exists *before* it paints — otherwise the fallback shows for a moment and is
 * then replaced, which reads as a glitch. Returns `undefined` while loading and
 * `null` when the slot is empty, so the two are distinguishable.
 */
export function usePlacementBanner(placement, params = {}) {
  const [banner, setBanner] = useState(undefined);
  const key = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    fetchPlacementBanners(placement, JSON.parse(key))
      .then((list) => { if (!cancelled) setBanner(list[0] || null); })
      .catch(() => { if (!cancelled) setBanner(null); });
    return () => { cancelled = true; };
  }, [placement, key]);

  return banner;
}

/**
 * A banner rendered into a slot a page already owns — the offer block's image
 * well, for instance. No gutter, no page rhythm; the host decides those.
 */
export function BannerSlot({ banner, className = '' }) {
  const [ratio, setRatio] = useState(null);
  const ref = useRef(null);
  const counted = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!banner || !node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !counted.current) {
        counted.current = true;
        trackBannerEvent(banner.banner_id, 'view');
        io.disconnect();
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [banner]);

  if (!banner?.desktop_image_url && !banner?.mobile_image_url) return null;

  const art = (
    <picture>
      <source media="(max-width: 640px)" srcSet={banner.mobile_image_url || banner.desktop_image_url} />
      <img
        src={banner.desktop_image_url}
        alt={banner.title || ''}
        onLoad={(e) => {
          const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
          if (w && h) setRatio(w / h);
        }}
        className="h-full w-full object-contain"
        loading="lazy"
        decoding="async"
      />
    </picture>
  );

  const box = (
    <div
      ref={ref}
      className={`overflow-hidden rounded-3xl ${className}`}
      /* The slot takes the artwork's ratio rather than imposing 16:9 — the
         hardcoded well was `aspect-video`, which would have cut a third off a
         2.75:1 banner. */
      style={{ aspectRatio: ratio ? String(ratio) : '1920 / 700', background: banner.background_color || 'transparent' }}
    >
      {art}
    </div>
  );

  return banner.cta_url
    ? <Link to={banner.cta_url} onClick={() => trackBannerEvent(banner.banner_id, 'click')} className="block">{box}</Link>
    : box;
}

/** Same component, named for the homepage/global slots that carry no category. */
export const PlacementBanner = (props) => <CategoryHeroBanner scoped={false} {...props} />;

export default CategoryHeroBanner;
